import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { tap } from "rxjs/operators";

import { PropertiesService } from "@app/core/services/properties.service";
import { TokenStorageService } from "@app/core/services/token-storage.service";
import { User } from "../domain";

export interface TokenExchangeResponse {
  access_token: string;
}

export interface MemberUpdateRequest {
  mobileNumber: string;
  firstName: string;
  lastName: string;
  email: string;
  birthday: string;
  country: string;
}

export interface NgrpApiClientInterface {
  exchangeCodeForToken(code: string): Observable<TokenExchangeResponse>;
  updateMember(request: MemberUpdateRequest, user: User): Observable<void>;
}

@Injectable()
export class NgrpApiClient implements NgrpApiClientInterface {
  constructor(
    private http: HttpClient,
    private properties: PropertiesService,
    private tokenStorage: TokenStorageService,
  ) {}

  /**
   * Exchange authorization code for access token via NGRP Ingo API
   * Stores the token in memory for the first authenticated request only
   */
  exchangeCodeForToken(code: string): Observable<TokenExchangeResponse> {
    try {
      const ngrpApiUrl = this.properties.get("ngrpApiUrl");

      if (!ngrpApiUrl || typeof ngrpApiUrl !== "string") {
        return throwError(
          new Error(
            "Property ngrpApiUrl not loaded. Cannot exchange authorization code.",
          ),
        );
      }

      const tokenEndpoint = "/api/v1/token";
      const url = `${ngrpApiUrl}${tokenEndpoint}`;

      return this.http
        .get<TokenExchangeResponse>(url, {
          params: { code },
        })
        .pipe(
          tap((tokenResponse: TokenExchangeResponse) => {
            this.tokenStorage.setToken(tokenResponse.access_token);
          }),
        );
    } catch (error) {
      return throwError(error);
    }
  }

  /**
   * Update member information including birth date via NGRP API
   */
  updateMember(request: MemberUpdateRequest, user: User): Observable<void> {
    try {
      const ngrpApiUrl = this.properties.get("ngrpApiUrl");

      if (!ngrpApiUrl || typeof ngrpApiUrl !== "string") {
        return throwError(
          new Error("Property ngrpApiUrl not loaded. Cannot update member."),
        );
      }

      const url = `${ngrpApiUrl}/api/v1/profile/${user.oktaId}`;

      return this.http.put<void>(url, request);
    } catch (error) {
      return throwError(error);
    }
  }
}
